import { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Download, FileText, X } from 'lucide-react';
import microSimService from '../../services/microSimService';
import SkillRadar from './SkillRadar';
import GroupHeatmap from './GroupHeatmap';
import ChoiceDistribution from './ChoiceDistribution';
import MicroSimReport from './MicroSimReport';
import { BandLegend } from './microSimBands';

/**
 * A launch's group results, for staff (D3: staff see everyone's scores).
 * Used in the launch console and on the session report.
 *
 * Tap people in the heatmap to overlay up to two of them on the room-average radar,
 * and open any one's full report. Re-fetches when `refreshKey` changes (the console
 * bumps it when a pair finishes, from the msim:progress socket event).
 */

const ROOM_COLOR = '#94A3B8';
const PERSON_COLORS = ['#6366F1', '#F97316'];

const Section = ({ title, right = null, children }) => (
  <section className="rounded-2xl border border-neutral-200 bg-white p-4">
    <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
      <h3 className="text-sm font-bold text-neutral-900">{title}</h3>
      {right}
    </div>
    {children}
  </section>
);
Section.propTypes = { title: PropTypes.string.isRequired, right: PropTypes.node, children: PropTypes.node };

const MicroSimGroupResults = ({ launchId, refreshKey = 0, showHeader = true }) => {
  const [results, setResults] = useState(null);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState([]); // people, max 2
  const [openPerson, setOpenPerson] = useState(null); // { person, report }
  const [busy, setBusy] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await microSimService.results(launchId);
      setResults(res.results);
      setError('');
    } catch (err) {
      setError(err?.userMessage || err?.message || 'Could not load results.');
    }
  }, [launchId]);

  useEffect(() => { load(); }, [load, refreshKey]);

  // Escape closes the person-report dialog.
  useEffect(() => {
    if (!openPerson) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpenPerson(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openPerson]);

  const keyOf = (p) => `${p.runId}-${p.slot}`;
  const toggle = (person) => {
    setSelected((prev) => {
      if (prev.some((p) => keyOf(p) === keyOf(person))) return prev.filter((p) => keyOf(p) !== keyOf(person));
      return [...prev, person].slice(-2);
    });
  };

  const run = async (label, fn) => {
    setBusy(label);
    setError('');
    try {
      await fn();
    } catch (err) {
      setError(err?.userMessage || err?.message || 'Something went wrong.');
    } finally {
      setBusy('');
    }
  };

  const openReport = (person) => run(`report-${keyOf(person)}`, async () => {
    const res = await microSimService.personReport(launchId, person.runId, person.slot);
    setOpenPerson({ person, report: res.report });
  });

  if (!results) {
    return error
      ? <p className="rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700">{error}</p>
      : <div className="flex justify-center py-10"><div className="h-8 w-8 animate-spin rounded-full border-[3px] border-neutral-200 border-t-primary-500" /></div>;
  }

  const { skills, people, roomAverage, pairs } = results;
  const series = [
    { name: 'Room average', color: ROOM_COLOR, values: roomAverage },
    ...selected.map((p, i) => ({ name: p.name, color: PERSON_COLORS[i], values: p.scores })),
  ];

  return (
    <div className="space-y-3">
      {showHeader && (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="text-base font-bold text-neutral-900">Results</h2>
            <p className="text-xs text-neutral-500">
              {pairs.completed} of {pairs.total} pairs finished · {people.length} people with results
              {pairs.playing ? ` · ${pairs.playing} still playing` : ''}
            </p>
          </div>
          <div className="flex gap-2">
            <button
              type="button"
              disabled={!!busy}
              onClick={() => run('csv', () => microSimService.downloadResultsCsv(launchId))}
              className="inline-flex items-center gap-1.5 rounded-xl border border-neutral-300 bg-white px-3 py-2 text-sm font-semibold text-neutral-800 hover:bg-neutral-50 disabled:opacity-60"
            >
              <Download className="h-4 w-4" /> {busy === 'csv' ? 'Exporting…' : 'CSV'}
            </button>
            <button
              type="button"
              disabled={!!busy}
              onClick={() => run('pdf', () => microSimService.downloadGroupPdf(launchId))}
              className="inline-flex items-center gap-1.5 rounded-xl bg-neutral-900 px-3 py-2 text-sm font-semibold text-white hover:bg-neutral-800 disabled:opacity-60"
            >
              <FileText className="h-4 w-4" /> {busy === 'pdf' ? 'Preparing…' : 'Group PDF'}
            </button>
          </div>
        </div>
      )}
      {error && <p className="rounded-xl bg-red-50 px-4 py-2 text-sm text-red-700">{error}</p>}

      <div className="grid gap-3 xl:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
        <Section title="Skill profile">
          <SkillRadar skills={skills} series={series} height={280} />
          <p className="mt-1 inline-flex items-center gap-2 text-sm text-neutral-600">
            <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: ROOM_COLOR }} /> Room average
          </p>
          {selected.length > 0 ? (
            <div className="mt-1.5 space-y-1.5">
              {selected.map((p, i) => (
                <div key={keyOf(p)} className="flex items-center justify-between gap-2 text-sm">
                  <span className="inline-flex min-w-0 items-center gap-2">
                    <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ backgroundColor: PERSON_COLORS[i] }} />
                    <span className="truncate font-medium text-neutral-800">{p.name}</span>
                  </span>
                  <span className="flex shrink-0 gap-3">
                    <button type="button" onClick={() => openReport(p)} className="text-xs font-semibold text-primary-600 hover:underline">
                      {busy === `report-${keyOf(p)}` ? 'Opening…' : 'View report'}
                    </button>
                    <button type="button" onClick={() => toggle(p)} className="text-xs text-neutral-400 hover:text-neutral-700">Remove</button>
                  </span>
                </div>
              ))}
            </div>
          ) : (
            <p className="mt-2 text-xs text-neutral-400">Tap people in the table to compare them with the room.</p>
          )}
        </Section>

        <Section title="Everyone’s skills (%)" right={<BandLegend />}>
          <GroupHeatmap skills={skills} people={people} roomAverage={roomAverage} onSelect={toggle} selectedKeys={selected.map(keyOf)} />
        </Section>
      </div>

      {pairs.completed > 0 && (
        <Section title="How rounds ended">
          <div className="space-y-2">
            {results.outcomes.map((r) => (
              <div key={r.round}>
                <p className="text-xs font-semibold text-neutral-500">Round {r.round + 1} · {r.roundTitle}</p>
                <div className="mt-1 flex flex-wrap gap-1.5">
                  {r.counts.map((c) => (
                    <span key={c.key} className={`rounded-full px-2.5 py-1 text-xs ${c.count ? 'bg-neutral-900 text-white' : 'bg-neutral-100 text-neutral-500'}`}>
                      {c.title} · <b>{c.count}</b>
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {pairs.completed > 0 && (
        <Section title="What the room chose">
          <ChoiceDistribution choices={results.choices} />
        </Section>
      )}

      {results.debriefQuestions?.length > 0 && (
        <Section title="Debrief questions">
          <ul className="list-disc space-y-1 pl-5 text-sm text-neutral-700">
            {results.debriefQuestions.map((q) => <li key={q}>{q}</li>)}
          </ul>
        </Section>
      )}

      <p className="text-xs text-neutral-400">{results.disclaimer || 'This is learning feedback, not a validated assessment.'}</p>

      {openPerson && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 sm:items-center"
          role="dialog"
          aria-modal="true"
          aria-label={`${openPerson.person.name} — practice results`}
          onClick={(e) => { if (e.target === e.currentTarget) setOpenPerson(null); }}
        >
          <div className="max-h-[92vh] w-full max-w-xl overflow-y-auto rounded-t-2xl bg-neutral-50 p-4 sm:rounded-2xl">
            <div className="mb-2 flex justify-end">
              <button type="button" onClick={() => setOpenPerson(null)} className="rounded-full p-1.5 text-neutral-500 hover:bg-neutral-200" aria-label="Close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <MicroSimReport
              compact
              report={openPerson.report}
              onDownloadPdf={() => microSimService.downloadPersonPdf(launchId, openPerson.person.runId, openPerson.person.slot)}
            />
          </div>
        </div>
      )}
    </div>
  );
};

MicroSimGroupResults.propTypes = {
  launchId: PropTypes.string.isRequired,
  refreshKey: PropTypes.number,
  showHeader: PropTypes.bool,
};

export default MicroSimGroupResults;
